import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { BarChart3, Bot, Shield, Zap, Globe, ArrowRight, Star, Sun, Moon, ArrowDown, Download } from 'lucide-react';
import { MetaTradifyLogo, MetaTradifyLogoSmall } from '@/components/MetaTradifyLogo';
import MarketTicker from '@/components/ui/marketTicker';
import { useState } from "react";
import { Menu, X } from "lucide-react";
const features = [
  { icon: BarChart3, title: 'Advanced Charts', desc: 'Professional TradingView charts with real-time data, indicators, and drawing tools.', span: 'md:col-span-7' },
  { icon: Bot, title: 'AI Trading Assistant', desc: 'Get AI-powered market analysis, signals, and risk assessment.', span: 'md:col-span-5' },
  { icon: Zap, title: 'Instant Execution', desc: 'Lightning-fast order execution with market, limit, and stop orders.', span: 'md:col-span-4' },
  { icon: Shield, title: 'Secure Platform', desc: 'Bank-grade encryption, JWT auth, and role-based access control.', span: 'md:col-span-4' },
  { icon: Globe, title: 'Live Market Data', desc: 'Real-time prices from CoinGecko for crypto, with 24/7 availability.', span: 'md:col-span-4' },
];

const plans = [
  { name: 'Starter', price: 'Free', desc: 'Perfect for beginners', features: ['$10,000 Demo Balance', 'Basic Charts', '5 Open Trades', 'Market Orders', 'Email Support'], popular: false },
  { name: 'Pro', price: '$29', desc: 'For active traders', features: ['Unlimited Balance', 'Advanced Charts', 'Unlimited Trades', 'All Order Types', 'AI Trading Signals', 'Priority Support'], popular: true },
  { name: 'Enterprise', price: '$99', desc: 'For institutions', features: ['Everything in Pro', 'API Access', 'Custom Indicators', 'Dedicated Manager', 'White-label Option', 'SLA Guarantee'], popular: false },
];

const testimonials = [
  { name: 'Sarah Chen', role: 'Crypto Trader', text: 'metaTradify transformed my trading strategy. The AI signals are incredibly accurate and the interface is a joy to use.', avatar: 'https://images.unsplash.com/photo-1576558656222-ba66febe3dec?w=80&h=80&fit=crop&crop=face' },
  { name: 'Marcus Johnson', role: 'Portfolio Manager', text: 'The most intuitive trading platform I have used. Real-time data and advanced charting make analysis effortless.', avatar: 'https://images.unsplash.com/photo-1689600944138-da3b150d9cb8?w=80&h=80&fit=crop&crop=face' },
  { name: 'Elena Rodriguez', role: 'Day Trader', text: 'From execution speed to AI insights, metaTradify delivers everything a professional trader needs in one place.', avatar: 'https://images.unsplash.com/photo-1655249493799-9cee4fe983bb?w=80&h=80&fit=crop&crop=face' },
];

export default function LandingPage() {
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] overflow-x-hidden">
      {/* Nav */}
      <MarketTicker />
      <nav className="fixed top-[36px] w-full z-50 glass border-b border-[var(--border-subtle)]" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <MetaTradifyLogo size={32} />
            <span className="text-lg font-bold font-['Outfit'] tracking-tight">metaTradify</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-[var(--text-secondary)]">
            <a href="#features" className="hover:text-[var(--text-primary)] transition-colors">Features</a>
            {/* <a href="#pricing" className="hover:text-[var(--text-primary)] transition-colors">Pricing</a> */}
            <a href="#testimonials" className="hover:text-[var(--text-primary)] transition-colors">Testimonials</a>
            <Link to="/download-app" className="hover:text-[var(--text-primary)] transition-colors">Download App</Link>
            <Link to="/about" className="hover:text-[var(--text-primary)] transition-colors">About</Link>
            <Link to="/contact" className="hover:text-[var(--text-primary)] transition-colors">Contact</Link>
        
          </div>
          <div className="flex items-center gap-3">

  <button
    onClick={toggleTheme}
    className="p-2 rounded-lg bg-[var(--hover-bg)] border border-[var(--border-subtle)]"
  >
    {theme === 'dark'
      ? <Sun className="w-4 h-4 text-amber-400" />
      : <Moon className="w-4 h-4 text-green-500" />}
  </button>

  {/* Desktop Login */}
  <Link
    to="/registration"
    className="hidden md:block px-4 py-2 text-sm bg-green-500 text-white rounded"
  >
    Registration
  </Link>

  {/* Mobile Menu Button */}
  <button
    className="md:hidden"
    onClick={() => setOpen(true)}
  >
    <Menu size={22} />
  </button>

</div>
        </div>
      </nav>
      {/* Mobile Sidebar */}
<>
  {/* Overlay */}
  <div
    className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[80]  transition ${
      open ? "opacity-100 visible" : "opacity-0 invisible"
    }`}
    onClick={() => setOpen(false)}
  />

  {/* Sidebar */}
  <div
    className={`fixed top-0 left-0 h-full w-[260px] bg-[#020617] z-[90]
    transition-all duration-300 ease-in-out
    ${open ? "translate-x-0" : "-translate-x-full"}`}
  >

    {/* Header */}
    <div className="flex items-center justify-between p-4 border-b border-white/10">
      <span className="text-white font-bold">Menu</span>
      <X className="text-white cursor-pointer" onClick={() => setOpen(false)} />
    </div>

    {/* Links */}
    <div className="flex flex-col gap-5 p-5 text-white/80 text-sm">

      <a href="#features" onClick={() => setOpen(false)}>Features</a>
      <a href="#testimonials" onClick={() => setOpen(false)}>Testimonials</a>
      <Link to="/download-app" onClick={() => setOpen(false)}>Download App</Link>
      <Link to="/about" onClick={() => setOpen(false)}>About</Link>
      <Link to="/contact" onClick={() => setOpen(false)}>Contact</Link>

      <Link
        to="/registration"
        className="mt-4 bg-green-500 py-2 rounded-lg text-white text-center"
        onClick={() => setOpen(false)}
      >
        Registration
      </Link>

    </div>

  </div>
</>
{/* <MarketTicker /> */}
      {/* Hero */}
      <section className="relative  pb-20 md:pt-44 md:pb-32 px-6 pt-[120px]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-emerald-500/8 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--border-subtle)] bg-[var(--hover-bg)] text-xs text-[var(--text-secondary)] mb-6 animate-fade-in-up">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live markets open 24/7
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Outfit'] tracking-tight leading-[1.1] mb-6 animate-fade-in-up stagger-1" data-testid="hero-heading">
                Trade smarter with
                <span className="text-green-400"> AI-powered</span> insights
              </h1>
              <p className="text-base md:text-lg text-[var(--text-secondary)] max-w-xl mb-8 animate-fade-in-up stagger-2">
                Professional-grade trading platform with real-time market data, advanced charting, and AI-driven analysis. Start trading crypto with confidence.
              </p>
              <div className="flex flex-wrap items-center gap-4 animate-fade-in-up stagger-3">
                <Link to="/download-app" data-testid="hero-cta-btn" className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all duration-200 hover:shadow-lg hover:shadow-green-500/25 animate-pulse-glow">
                  Download App <ArrowDown className="w-4 h-4" />
                </Link>
                <Link to="/registration" className="inline-flex items-center gap-2 px-6 py-3 border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-subtle)] rounded text-sm transition-all duration-200">
                  Registration <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-10 text-sm text-[var(--text-secondary)] animate-fade-in-up stagger-4">
                <span className="font-mono">10K+ Traders</span>
                <span className="w-1 h-1 rounded-full bg-zinc-600" />
                <span className="font-mono">$500M+ Volume</span>
                <span className="w-1 h-1 rounded-full bg-zinc-600" />
                <span className="font-mono">24/7 Markets</span>
              </div>
            </div>
            {/* Hero Trading Image */}
            <div className="hidden lg:block animate-fade-in-up stagger-2" data-testid="hero-image">
              <div className="relative">
                <div className="absolute -inset-4 bg-green-500/10 rounded-2xl blur-2xl" />
                <div className="relative rounded-xl overflow-hidden border border-[var(--border-subtle)] shadow-2xl shadow-green-500/5">
                  <img
                    src="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&h=600&fit=crop&q=80"
                    alt="Trading dashboard showing real-time market charts and data"
                    className="w-full h-auto object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)]/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-lg border border-white/10">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-xs text-white/90 font-mono">LIVE</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-lg border border-white/10">
                        <span className="text-xs text-emerald-400 font-mono">BTC +2.4%</span>
                      </div>
                      <div className="px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-lg border border-white/10">
                        <span className="text-xs text-emerald-400 font-mono">ETH +1.8%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-14">
            <h2 className="text-base md:text-lg font-semibold font-['Outfit'] text-green-400 mb-2">Platform Features</h2>
            <p className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight">Everything you need to trade</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className={`${f.span} p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded hover:border-[var(--border-subtle)] transition-all duration-300 group`}>
                  <div className="w-10 h-10 rounded bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-colors">
                    <Icon className="w-5 h-5 text-green-400" />
                  </div>
                  <h3 className="text-base font-semibold font-['Outfit'] mb-2">{f.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      {/* <section id="pricing" className="py-20 md:py-28 px-6 bg-[var(--bg-primary)]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-base md:text-lg font-semibold font-['Outfit'] text-green-400 mb-2">Pricing</h2>
            <p className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight">Choose your plan</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {plans.map((plan, i) => (
              <div key={i} className={`relative p-6 rounded border transition-all duration-300 ${plan.popular ? 'bg-[var(--bg-surface)] border-green-500/50 shadow-lg shadow-green-500/10' : 'bg-[var(--bg-surface)] border-[var(--border-subtle)] hover:border-[var(--border-subtle)]'}`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
                    Most Popular
                  </div>
                )}
                <h3 className="text-lg font-semibold font-['Outfit'] mb-1">{plan.name}</h3>
                <p className="text-xs text-[var(--text-secondary)] mb-4">{plan.desc}</p>
                <div className="mb-6">
                  <span className="text-3xl font-bold font-mono">{plan.price}</span>
                  {plan.price !== 'Free' && <span className="text-sm text-[var(--text-secondary)]">/month</span>}
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                      <div className="w-1 h-1 rounded-full bg-green-400" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/login" className={`block text-center py-2.5 rounded text-sm font-medium transition-all duration-200 ${plan.popular ? 'bg-green-500 hover:bg-green-600 text-white' : 'border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-white hover:border-[var(--border-subtle)]'}`}>
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Testimonials */}
      <section id="testimonials" className="py-20 md:py-28 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-base md:text-lg font-semibold font-['Outfit'] text-green-400 mb-2">Testimonials</h2>
            <p className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight">Loved by traders worldwide</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded hover:border-[var(--border-subtle)] transition-all duration-300">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-yellow-500 fill-yellow-500" />)}
                </div>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-xs text-[var(--text-secondary)]">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] tracking-tight mb-4">Ready to start trading?</h2>
          <p className="text-sm text-[var(--text-secondary)] mb-8">Join thousands of traders already using metaTradify. Start with a free demo account.</p>
          <Link to="/login" data-testid="cta-register-btn" className="inline-flex items-center gap-2 px-8 py-3.5 bg-green-500 hover:bg-green-600 text-white rounded text-sm font-medium transition-all duration-200 hover:shadow-lg hover:shadow-green-500/25">
            Create Free Account <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--border-subtle)] py-10 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <MetaTradifyLogoSmall size={24} />
            <span className="text-sm font-semibold font-['Outfit']">metaTradify</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[var(--text-tertiary)]">
            <Link to="/about" className="hover:text-[var(--text-secondary)] transition-colors">About Us</Link>
            <Link to="/contact" className="hover:text-[var(--text-secondary)] transition-colors">Contact Us</Link>
            <Link to="/privacy-policy" className="hover:text-[var(--text-secondary)] transition-colors">Privacy Policy</Link>
          </div>
          <p className="text-xs text-[var(--text-tertiary)]">&copy; 2025 metaTradify. All rights reserved. Trading involves risk.</p>
        </div>
      </footer>
    </div>
  );
}
